/**
 *
 *  TimeList
 *  DateTimePicker
 *
 *  Created by Vladimir Shishlyannikov on 12/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import PropTypes from "prop-types"; // use directly

// import moment from "moment-timezone";

// Components
import TimeZoneLabel from "../../TimeZoneLabel/TimeZoneLabel";

// Utils
import * as dates from "../../../utils/dates";
import { getMomentWithTz } from "../../../utils/date";

var format = (props) => "h:mm"; // eslint-disable-line

class TimeList extends React.Component {
  static propTypes = {
    value: PropTypes.instanceOf(Date),
    step: PropTypes.number,
    /**
     * List of time slots for a particular day.
     *
     * @example ['prop', ['timeSlots', '[{ startTime: new Date(), endTime: new Date() }, ..]']]
     */
    timeSlots: PropTypes.array,
    min: PropTypes.instanceOf(Date),
    max: PropTypes.instanceOf(Date),
    currentDate: PropTypes.instanceOf(Date),

    itemComponent: PropTypes.func,
    format: PropTypes.string,
    onSelect: PropTypes.func,
    preserveDate: PropTypes.bool,
    culture: PropTypes.string,
    region: PropTypes.string,
  };

  static defaultProps = {
    step: 30,
    onSelect: () => {},
    currentDate: new Date(),
    min: new Date(1900, 0, 1),
    max: new Date(2099, 11, 31),
    preserveDate: true,
    region: process.env.REACT_APP_REGION,
    // ariaActiveDescendantKey: 'timelist'
  };

  constructor(props) {
    super(props);

    this.listRef = React.createRef();

    this.state = this.getStateFromProps(props);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { value, timeSlots } = this.props;
    var valChanged = !dates.eq(nextProps.value, value, "minutes"),
      timeSlotsChanged =
        JSON.stringify(nextProps.timeSlots) !== JSON.stringify(timeSlots);

    return valChanged || timeSlotsChanged;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(this.getStateFromProps(nextProps));
  }

  componentWillUnmount() {}

  getBounds(props) {
    var value = props.value || props.currentDate || dates.today(),
      useDate = props.preserveDate,
      min = props.min,
      max = props.max,
      start,
      end;

    // compare just the time regardless of whether they fall on the same day
    if (!useDate) {
      start = dates.startOf(
        dates.merge(new Date(), min, props.currentDate, props.region),
        "minutes"
      );

      end = dates.startOf(
        dates.merge(new Date(), max, props.currentDate, props.region),
        "minutes"
      );

      if (dates.lte(end, start) && dates.gt(max, min, "day"));

      end = dates.tomorrow();

      return {
        min: start,
        max: end,
      };
    }

    start = getMomentWithTz(new Date(), props.region).startOf("day"); // dates.today();
    end = start.clone().add(1, "day"); // dates.tomorrow();

    // date parts are equal
    return {
      min: dates.eq(value, min, "day")
        ? dates.merge(start.toDate(), min, props.currentDate, props.region)
        : start.toDate(),
      max: dates.eq(value, max, "day")
        ? dates.merge(start.toDate(), max, props.currentDate, props.region)
        : end.toDate(),
    };
  }

  timeSlotExists(timeSlots, start) {
    return !!timeSlots.find(
      (slot) =>
        dates.hours(start) === dates.hours(slot.start) &&
        dates.minutes(start) === dates.minutes(slot.start)
    );
  }

  getDates(props = this.props) {
    var times = [];
    var value = props.value || props.currentDate || dates.today();
    var values = this.getBounds(props);
    var start = getMomentWithTz(values.min, props.region);
    var startDay = start.date(); // dates.date(start);

    while (start.date() === startDay && start.isSameOrBefore(values.max)) {
      let found = null;
      let disabled = false;

      if (!props.timeSlots || !props.timeSlots.length) {
        disabled = true;
      } else {
        // We check only hours and then merge date and time in DateTimePicker
        found = this.timeSlotExists(props.timeSlots, start.toDate());
        disabled = !found;
      }

      let noon = start.clone().startOf("day").add(810, "minutes"); // 13:30
      let evening = noon.clone().add(5, "hours"); // 18:30

      let daysEqual = getMomentWithTz(value, props.region).isSame(
        new Date(),
        "day"
      );
      let monthsEqual = getMomentWithTz(value, props.region).isSame(
        new Date(),
        "month"
      );

      if (
        found &&
        (!daysEqual ||
          start.isSameOrAfter(new Date()) ||
          (daysEqual && !monthsEqual))
      ) {
        times.push({
          date: start.toDate(),
          disabled:
            disabled ||
            (found &&
              dates.eq(found.date, new Date(), "day") &&
              start.isSameOrBefore(new Date())),
          slotType: found && found.slotType,
          label: start.format(format(props)),
          labelAmPm: start.format("A"),
          dayPart: start.isSameOrBefore(noon)
            ? "morning"
            : start.isAfter(noon) && start.isBefore(evening)
            ? "afternoon"
            : "evening",
        });
      }

      start.add(props.step || 30, "minutes");
    }

    return times;
  }

  getStateFromProps(props = this.props) {
    var { value, currentDate } = props;
    var data = this.getDates(props);
    var currentValue = value || currentDate;
    var closestDate = this._closestDate(data, currentValue);

    return {
      dates: data,
      selectedItem: closestDate,
      focusedItem: null,
    };
  }

  scrollTo() {
    if (this.listRef.move) this.listRef.move();
  }

  render() {
    var { onSelect } = this.props;
    var { selectedItem } = this.state;

    return (
      <div className="tb-timelist tbk-max-w-94 tbk-py-3 tbk-px-2">
        <div
          className="tb-timelist-header tbk-flex tbk-items-center tbk-justify-between"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="tbk-text-cta tbk-uppercase tbk-text-primary">
            and time
          </span>
          {/*<span className="tbk-text-small tbk-text-grey-inactive">*/}
          {/*  time set to your local timezone*/}
          {/*</span>*/}
        </div>
        <div className="tbk-text-small tbk-mt-0.5 tbk-text-blue-grey">
          Time set to <TimeZoneLabel region={this.props.region} />
        </div>
        <ul
          ref={this.listRef}
          className="tbk-grid tbk-grid-cols-5 tbk-gap-1 tbk-pt-1.5 tbk-outline-none"
        >
          {this.state.dates && this.state.dates.length ? (
            this.state.dates.map((item) => (
              <li
                key={`${item.label} ${item.labelAmPm}`}
                className={`tbk-text-main tbk-h-6 tbk-w-6 tbk-rounded-full tbk-py-2 tbk-px-0 !tbk-text-lg !tbk-leading-4 ${
                  dates.eq(item.date, selectedItem)
                    ? item.disabled
                      ? "tbk-bg-basic-white tbk-text-grey-inactive"
                      : "tbk-bg-coral tbk-text-basic-white"
                    : "hover:md:tbk-text-blue-grey-main tbk-text-grey-main hover:md:tbk-bg-blue-grey-light"
                } tbk-cursor-pointer tbk-select-none tbk-text-center tbk-outline-none`}
                onClick={() => onSelect(item)}
              >
                {item.label}
                <div className="tbk-text-small !tbk-leading-none">
                  {item.labelAmPm}
                </div>
              </li>
            ))
          ) : (
            <div className="tbk-text-main tbk-whitespace-nowrap tbk-text-grey-main">
              There are no time slots available
            </div>
          )}
        </ul>
      </div>
    );
  }

  _closestDate(times, date) {
    var roundTo = 1000 * 60 * this.props.step,
      inst = null,
      label,
      labelAmPm;

    if (!date) return null;

    date = new Date(Math.floor(date.getTime() / roundTo) * roundTo);
    label = getMomentWithTz(date, this.props.region).format(format(this.props));
    labelAmPm = getMomentWithTz(date, this.props.region).format("A");

    // eslint-disable-next-line array-callback-return
    times.some((time) => {
      if (
        time.label === label &&
        time.labelAmPm === labelAmPm &&
        !time.disabled
      ) {
        return (inst = time.date);
      }
    });

    return inst;
  }
}

export default TimeList;
